import { supabase } from '../lib/supabase';
import { userService } from './userService';
import { parseJsonArray } from '../lib/jsonColumn';
import { getCampaignBaseFee, computeCampaignCommission } from '../utils/commissionCalculations';

interface MohitInvoiceJson {
  id: string;
  status: string;
  invoice_number: string;
  purchase_order: string;
  issued_date: string;
  due_date: string;
  amount_received: number;
  payment_received_date: string;
  client_invoice_number: string;
}

interface InfluencerInvoiceJson {
  id: string;
  invoice_number: string;
  received_date: string;
  amount: number;
  status: string;
  payment_date: string;
}

interface InvoiceWithPayment {
  id: string;
  type: 'Client' | 'Influencer';
  number: string;
  status: string;
  total: number;
  issue_date: string;
  payment_date: string | null;
}

interface CampaignExportData {
  id: string;
  year: number;
  work_code: string;
  campaign_ref: string | null;
  campaign_mode: 'brand_action' | 'influencer_marketing';
  client_name: string;
  brand: string | null;
  campaign_status: string;
  agent_name: string | null;
  fee: number;
  commission_pct: number;
  campaign_budget: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  influencers: Array<{
    name: string;
    agreed_fee: number;
    status: string;
  }>;
  deliverables: Array<{
    platform: string;
    type: string;
    title: string;
    status: string;
    due_date: string | null;
  }>;
  mohitInvoices: MohitInvoiceJson[];
  influencerInvoices: InfluencerInvoiceJson[];
  invoices: InvoiceWithPayment[];
}

export const csvExportService = {
  mapStatusToPortuguese(status: string): string {
    const statusMap: Record<string, string> = {
      'Paid': 'Paga',
      'Issued': 'Emitida',
      'Partially Paid': 'Parcialmente Paga',
      'Draft': 'Rascunho',
      'Overdue': 'Vencida',
      'Canceled': 'Cancelada',
      'Not Issued': 'Não Emitida',
      'Not Requested': 'Não Solicitada',
      'Requested': 'Solicitada',
      'Received': 'Recebida'
    };
    return statusMap[status] || status;
  },

  formatDateToPT(dateString: string | null): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  },

  async checkAdminPermission(): Promise<boolean> {
    const userRole = await userService.getCurrentUserRole();
    return userRole?.role === 'admin';
  },

  async fetchAllCampaignData(): Promise<CampaignExportData[]> {
    const isAdmin = await this.checkAdminPermission();
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin permission required');
    }

    const { data: campaigns, error: campaignsError } = await supabase
      .from('campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (campaignsError) throw campaignsError;

    const campaignIds = campaigns?.map(c => c.id) || [];

    const { data: influencers, error: influencersError } = await supabase
      .from('campaign_influencers')
      .select('campaign_id, influencer_name, agreed_fee, status')
      .in('campaign_id', campaignIds);

    if (influencersError) throw influencersError;

    const { data: deliverables, error: deliverablesError } = await supabase
      .from('deliverables')
      .select('campaign_id, platform, type, title, status, due_date')
      .in('campaign_id', campaignIds);

    if (deliverablesError) throw deliverablesError;

    // Fetch invoices from the invoices table with payments
    const { data: invoices, error: invoicesError } = await supabase
      .from('invoices')
      .select(`
        id,
        campaign_id,
        type,
        number,
        status,
        total,
        issue_date,
        payments (
          paid_at
        )
      `)
      .in('campaign_id', campaignIds)
      .order('issue_date', { ascending: true });

    if (invoicesError) throw invoicesError;

    const exportData: CampaignExportData[] = (campaigns || []).map(campaign => {
      const campaignInfluencers = (influencers || [])
        .filter(inf => inf.campaign_id === campaign.id)
        .map(inf => ({
          name: inf.influencer_name,
          agreed_fee: inf.agreed_fee || 0,
          status: inf.status || 'Unknown'
        }));

      const campaignDeliverables = (deliverables || [])
        .filter(del => del.campaign_id === campaign.id)
        .map(del => ({
          platform: del.platform || 'N/A',
          type: del.type || 'N/A',
          title: del.title || 'N/A',
          status: del.status || 'Draft',
          due_date: del.due_date
        }));

      const mohitInvoices = parseJsonArray<MohitInvoiceJson>(campaign.mohit_invoices);
      const influencerInvoices = parseJsonArray<InfluencerInvoiceJson>(campaign.influencer_invoices);

      // Process invoices from the invoices table
      const campaignInvoices = (invoices || [])
        .filter(inv => inv.campaign_id === campaign.id)
        .map(inv => {
          // Get the latest payment date from payments array
          const payments = Array.isArray(inv.payments) ? inv.payments : [];
          const latestPayment = payments
            .map((p: any) => p.paid_at)
            .filter((date: any) => date)
            .sort()
            .pop();

          return {
            id: inv.id,
            type: inv.type,
            number: inv.number || 'N/A',
            status: inv.status || 'Draft',
            total: inv.total || 0,
            issue_date: inv.issue_date || '',
            payment_date: latestPayment || null
          };
        });

      return {
        id: campaign.id,
        year: campaign.year,
        work_code: campaign.work_code,
        campaign_ref: campaign.campaign_ref,
        campaign_mode: campaign.campaign_mode || 'brand_action',
        client_name: campaign.client,
        brand: campaign.brand,
        campaign_status: campaign.campaign_status || 'Consultation',
        agent_name: campaign.agent_name,
        // Base fee and an effective rate so fee × pct is the real commission
        // (sponsorship_amount/agency cut, flat fees and revenue-only services)
        fee: getCampaignBaseFee(campaign),
        commission_pct: getCampaignBaseFee(campaign) > 0
          ? computeCampaignCommission(campaign) / getCampaignBaseFee(campaign)
          : 0,
        campaign_budget: campaign.campaign_budget,
        notes: campaign.notes,
        created_at: campaign.created_at,
        updated_at: campaign.updated_at,
        influencers: campaignInfluencers,
        deliverables: campaignDeliverables,
        mohitInvoices,
        influencerInvoices,
        invoices: campaignInvoices
      };
    });

    return exportData;
  },

  escapeCSVValue(value: any): string {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  },

  generateCSV(data: CampaignExportData[]): string {
    const headers = [
      'Year',
      'Work Code',
      'Campaign Ref',
      'Campaign Mode',
      'Client',
      'Brand',
      'Campaign Status',
      'Agent',
      'Fee',
      'Commission %',
      'Commission Amount',
      'Campaign Budget',
      'Influencers',
      'Influencer Count',
      'Deliverables Count',
      'Client Invoices Count',
      'Client Invoice Numbers',
      'Client Invoice Status (PT)',
      'Client Invoice Payment Dates',
      'Influencer Invoices Count',
      'Influencer Invoice Numbers',
      'Influencer Invoice Status (PT)',
      'Influencer Invoice Payment Dates',
      'Total Client Invoice Amount',
      'Total Influencer Invoice Amount',
      'Created Date',
      'Updated Date',
      'Notes'
    ];

    const rows = data.map(campaign => {
      const commissionAmount = campaign.fee * campaign.commission_pct;
      const influencerNames = campaign.influencers.map(inf => inf.name).join('; ');

      // Separate new invoices by type
      const clientInvoices = campaign.invoices.filter(inv => inv.type === 'Client');
      const influencerInvoices = campaign.invoices.filter(inv => inv.type === 'Influencer');

      // Legacy invoice data (from mohit_invoices / influencer_invoices JSON columns)
      const legacyClientInvoiceNumbers = campaign.mohitInvoices
        .map(inv => inv.invoice_number)
        .filter(num => num && num.trim() !== '')
        .join('; ');
      const legacyClientStatuses = campaign.mohitInvoices
        .map(inv => this.mapStatusToPortuguese(inv.status))
        .filter(s => s && s.trim() !== '')
        .join('; ');
      const legacyClientPaymentDates = campaign.mohitInvoices
        .map(inv => this.formatDateToPT(inv.payment_received_date))
        .filter(d => d.trim() !== '')
        .join('; ');

      const legacyInfluencerInvoiceNumbers = campaign.influencerInvoices
        .map(inv => inv.invoice_number)
        .filter(num => num && num.trim() !== '')
        .join('; ');
      const legacyInfluencerStatuses = campaign.influencerInvoices
        .map(inv => this.mapStatusToPortuguese(inv.status))
        .filter(s => s && s.trim() !== '')
        .join('; ');
      const legacyInfluencerPaymentDates = campaign.influencerInvoices
        .map(inv => this.formatDateToPT(inv.payment_date))
        .filter(d => d.trim() !== '')
        .join('; ');

      // New invoice data (from invoices + payments tables)
      const newClientInvoiceNumbers = clientInvoices
        .map(inv => inv.number)
        .filter(num => num && num !== 'N/A')
        .join('; ');
      const newClientStatuses = clientInvoices
        .map(inv => this.mapStatusToPortuguese(inv.status))
        .join('; ');
      const newClientPaymentDates = clientInvoices
        .map(inv => this.formatDateToPT(inv.payment_date))
        .filter(d => d.trim() !== '')
        .join('; ');

      const newInfluencerInvoiceNumbers = influencerInvoices
        .map(inv => inv.number)
        .filter(num => num && num !== 'N/A')
        .join('; ');
      const newInfluencerStatuses = influencerInvoices
        .map(inv => this.mapStatusToPortuguese(inv.status))
        .join('; ');
      const newInfluencerPaymentDates = influencerInvoices
        .map(inv => this.formatDateToPT(inv.payment_date))
        .filter(d => d.trim() !== '')
        .join('; ');

      // Combine legacy and new data
      const allClientInvoiceNumbers = [legacyClientInvoiceNumbers, newClientInvoiceNumbers]
        .filter(s => s.trim() !== '')
        .join('; ');
      const allInfluencerInvoiceNumbers = [legacyInfluencerInvoiceNumbers, newInfluencerInvoiceNumbers]
        .filter(s => s.trim() !== '')
        .join('; ');
      const clientInvoiceStatuses = [legacyClientStatuses, newClientStatuses]
        .filter(s => s.trim() !== '')
        .join('; ');
      const influencerInvoiceStatuses = [legacyInfluencerStatuses, newInfluencerStatuses]
        .filter(s => s.trim() !== '')
        .join('; ');
      const clientInvoicePaymentDates = [legacyClientPaymentDates, newClientPaymentDates]
        .filter(s => s.trim() !== '')
        .join('; ');
      const influencerInvoicePaymentDates = [legacyInfluencerPaymentDates, newInfluencerPaymentDates]
        .filter(s => s.trim() !== '')
        .join('; ');

      // Calculate totals
      const totalLegacyClientInvoice = campaign.mohitInvoices
        .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
      const totalLegacyInfluencerInvoice = campaign.influencerInvoices
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);
      const totalNewClientInvoice = clientInvoices
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      const totalNewInfluencerInvoice = influencerInvoices
        .reduce((sum, inv) => sum + (inv.total || 0), 0);

      const totalClientInvoice = totalLegacyClientInvoice + totalNewClientInvoice;
      const totalInfluencerInvoice = totalLegacyInfluencerInvoice + totalNewInfluencerInvoice;

      const totalClientInvoiceCount = campaign.mohitInvoices.length + clientInvoices.length;
      const totalInfluencerInvoiceCount = campaign.influencerInvoices.length + influencerInvoices.length;

      return [
        campaign.year,
        campaign.work_code,
        campaign.campaign_ref || '',
        campaign.campaign_mode,
        campaign.client_name,
        campaign.brand || '',
        campaign.campaign_status,
        campaign.agent_name || '',
        campaign.fee.toFixed(2),
        (campaign.commission_pct * 100).toFixed(2) + '%',
        commissionAmount.toFixed(2),
        campaign.campaign_budget?.toFixed(2) || '',
        influencerNames,
        campaign.influencers.length,
        campaign.deliverables.length,
        totalClientInvoiceCount,
        allClientInvoiceNumbers,
        clientInvoiceStatuses,
        clientInvoicePaymentDates,
        totalInfluencerInvoiceCount,
        allInfluencerInvoiceNumbers,
        influencerInvoiceStatuses,
        influencerInvoicePaymentDates,
        totalClientInvoice.toFixed(2),
        totalInfluencerInvoice.toFixed(2),
        new Date(campaign.created_at).toLocaleDateString(),
        new Date(campaign.updated_at).toLocaleDateString(),
        campaign.notes || ''
      ].map(this.escapeCSVValue);
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  },

  async exportCampaignsToCSV(): Promise<void> {
    const data = await this.fetchAllCampaignData();
    const csv = this.generateCSV(data);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `campaigns_export_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
